<template>
  <v-app id="login" class="login-background">
    <v-container fluid fill-height>
      <v-layout align-center justify-center>
        <v-flex xs3>
          <v-card>
            <v-card dark color="primary">
              <v-card-text>
                <v-toolbar-title>Login</v-toolbar-title>
              </v-card-text>
            </v-card>

            <v-flex xs12>
              <v-layout justify-center class="mt-3">
                <v-btn-toggle
                  mandatory
                  v-model="loginMode"
                  class="toggle-button-group"
                >
                  <v-btn flat value="windows" class="toggle-button">
                    UTA Windows Login
                  </v-btn>
                  <v-btn flat value="plane" class="toggle-button">
                    Custom Login
                  </v-btn>
                </v-btn-toggle>
              </v-layout>
            </v-flex>
            <v-card-text class="pb-2">
              <v-form>
                <v-text-field
                  prepend-icon="mdi-account"
                  name="username"
                  label="User Name"
                  type="text"
                  ref="username"
                  v-model="username"
                  :rules="[rules.required]"
                ></v-text-field>
                <v-text-field
                  prepend-icon="mdi-lock"
                  autocomplete
                  name="password"
                  label="Password"
                  id="password"
                  type="password"
                  ref="password"
                  v-model="password"
                  :rules="[rules.required]"
                ></v-text-field>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-layout column>
                <v-btn
                  class="mb-3"
                  dark
                  :loading="loggingIn"
                  color="green"
                  v-on:click="login()"
                  >Login</v-btn
                >
              </v-layout>
            </v-card-actions>
            <v-card-text
              v-if="loginMode == 'windows' && isMicrosoftLoginHost"
              class="pt-0 pb-3"
            >
              <div class="microsoft-login-separator">
                <span>or</span>
              </div>
              <v-btn
                block
                outline
                color="primary"
                :loading="loggingIn"
                class="microsoft-login-btn"
                @click="loginWithMicrosoft()"
              >
                <span class="microsoft-logo" aria-hidden="true">
                  <span class="ms-tile ms-red"></span>
                  <span class="ms-tile ms-green"></span>
                  <span class="ms-tile ms-blue"></span>
                  <span class="ms-tile ms-yellow"></span>
                </span>
                Sign in with Microsoft
              </v-btn>
            </v-card-text>

            <v-card-text v-if="invalidLogin" class="pt-0">
              <p class="text-danger">
                {{ loginError }}
              </p>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </v-app>
</template>

<script>
import { PublicClientApplication } from "@azure/msal-browser/dist/index.js";
import paths from "../router/paths";

export default {
  data: () => ({
    username: "",
    password: "",
    name: "",
    invalidLogin: false,
    loggingIn: false,
    loginMode: "windows",
    isMicrosoftLoginHost: ["localhost", "userportaltest.utaw.org"].includes(
      window.location.hostname
    ),
    loginError: "",
    pages: [],

    rules: {
      required: (value) => !!value || "Required.",
    },
  }),
  async created() {
    window.addEventListener("keyup", (e) => this.onKeyPressed(e));

    await this.$store.dispatch("authentication/AUTH_LOGOUT");

    this.loadPages();

    const loginMode = localStorage.getItem("login-mode");

    this.loginMode = loginMode ? loginMode : "windows";
  },
  destroyed() {
    window.removeEventListener("keyup", (e) => this.onKeyPressed(e));
  },
  methods: {
    onKeyPressed(e) {
      if (e.keyCode == 13) {
        this.login();
      }
    },
    loadPages() {
      this.pages = paths;
    },
    async login() {
      if (
        !this.$refs.username.validate(true) ||
        !this.$refs.password.validate(true)
      )
        return;

      this.loggingIn = true;

      this.$Progress.start();

      const username = this.getUsername();

      try {
        const user = await this.$store.dispatch("authentication/AUTH_REQUEST", {
          username: username,
          password: this.password,
          windowsLogin: this.loginMode == "windows",
        });

        localStorage.setItem("login-mode", this.loginMode);

        this.$router.push({
          name: this.getRedirectLink(user),
          params: this.$route.query.params,
        });

        this.$Progress.finish();
      } catch (error) {
        this.loggingIn = false;

        this.$Progress.finish();

        this.invalidLogin = true;
        this.loginError = error;
      }
    },
    async loginWithMicrosoft() {
      this.invalidLogin = false;
      this.loginError = "";
      this.loggingIn = true;
      this.$Progress.start();

      try {
        const clientId =
          process.env.VUE_APP_MSAL_CLIENT_ID ||
          "2b10fd94-e0d7-4e95-a1b6-7f7787393767";

        if (!clientId) {
          throw "VUE_APP_MSAL_CLIENT_ID is not configured.";
        }

        const tenantId =
          process.env.VUE_APP_MSAL_TENANT_ID ||
          "619cfa6d-db4b-4833-944c-18413ab03e5d";
        const authority =
          process.env.VUE_APP_MSAL_AUTHORITY ||
          `https://login.microsoftonline.com/${tenantId}`;
        const redirectUri = window.location.origin;
        const scopes = (
          process.env.VUE_APP_MSAL_SCOPES || "openid,profile,email"
        )
          .split(",")
          .map((scope) => scope.trim())
          .filter((scope) => !!scope);

        const msalApp = new PublicClientApplication({
          auth: {
            clientId: clientId,
            authority: authority,
            redirectUri: redirectUri,
          },
        });

        const popupResponse = await msalApp.loginPopup({
          scopes: scopes,
        });

        console.log(popupResponse);

        const user = await this.$store.dispatch("authentication/AUTH_REQUEST", {
          microsoftLogin: true,
          microsoftToken: popupResponse.idToken,
          username: popupResponse.account.username,
        });

        localStorage.setItem("login-mode", this.loginMode);

        this.$router.push({
          name: this.getRedirectLink(user),
          params: this.$route.query.params,
        });

        this.$Progress.finish();
      } catch (error) {
        this.loggingIn = false;
        this.$Progress.finish();
        this.invalidLogin = true;
        this.loginError = this.getMicrosoftLoginErrorMessage(error);
      }
    },
    getMicrosoftLoginErrorMessage(error) {
      const message = (
        typeof error === "string"
          ? error
          : error && error.message
          ? error.message
          : ""
      ).toLowerCase();
      const code =
        error && error.errorCode ? error.errorCode.toLowerCase() : "";

      if (code === "user_cancelled" || message.includes("user_cancelled")) {
        return "Microsoft sign-in was canceled. Please click 'Sign in with Microsoft' to try again.";
      }

      if (code === "popup_window_error" || message.includes("popup")) {
        return "We could not open the Microsoft sign-in window. Please allow popups for this site and try again.";
      }

      if (
        code === "popup_window_timeout" ||
        message.includes("timed out") ||
        message.includes("timeout")
      ) {
        return "Microsoft sign-in timed out. Please try again.";
      }

      return "Microsoft sign-in could not be completed. Please try again.";
    },
    getUsername() {
      if (this.loginMode != "windows") return this.username;

      return this.username.includes("@utaw.org")
        ? this.username
        : this.username + "@utaw.org";
    },
    isAdmin() {
      const isAdmin = this.$store.getters["authentication/isAdmin"];

      if (!isAdmin || isAdmin == "false") return false;

      return true;
    },
    getRedirectLink(user) {
      return "UtaDashboard";

      // if (!this.isAdmin()) {
      //     const page = this.pages.find(
      //         (path) =>
      //             !path.path.includes(":") &&
      //             user.claims.includes(path.name),
      //     );

      //     return page ? page.name : user.claims[0];
      // }

      // return this.$route.query.redirect
      //     ? this.$route.query.redirect
      //     : "Accounts";
    },
  },
};
</script>
<style>
.toggle-button {
  font-size: 15px;
  padding: 5px !important;
}
.toggle-button-group.v-btn-toggle .v-btn.v-btn--active {
  background-color: #dcdfe6;
}

.microsoft-login-separator {
  align-items: center;
  color: #8a8a8a;
  display: flex;
  font-size: 12px;
  margin: 2px 0 14px;
  text-transform: uppercase;
}

.microsoft-login-separator::before,
.microsoft-login-separator::after {
  border-top: 1px solid #e0e0e0;
  content: "";
  flex: 1;
}

.microsoft-login-separator span {
  padding: 0 10px;
}

.microsoft-login-btn {
  align-items: center;
  display: inline-flex;
  text-transform: none;
}

.microsoft-logo {
  display: inline-grid;
  gap: 1px;
  grid-template-columns: repeat(2, 7px);
  grid-template-rows: repeat(2, 7px);
  margin-right: 10px;
}

.ms-tile {
  height: 7px;
  width: 7px;
}

.ms-red {
  background-color: #f25022;
}

.ms-green {
  background-color: #7fba00;
}

.ms-blue {
  background-color: #00a4ef;
}

.ms-yellow {
  background-color: #ffb900;
}

.login-background {
  background-image: url(https://utaw.blob.core.windows.net/images/logo+uta11.png) !important;
  /* height: 100% !important; */
  background-position: right 50% bottom 95% !important;
  background-repeat: no-repeat !important;
  background-size: 10% !important;
}
</style>
